/**
 * Lightweight traveller list for the onboarding flow.
 *
 * All cards are always expanded with just the essential fields:
 * first name, last name, relationship, and date of birth.
 * No collapse/expand, no "Done" buttons, no passport or dietary fields.
 */

import type { AdditionalTraveller, Relationship } from '@elara/domain/models/schemas/TravelerProfileSchema';

import { useState } from 'react';

import { preferencesToText, textToPreferences } from '@elara/client-core/lib/preference-utils';
import { cn } from '@elara/client-core/lib/utils';

import { Button, Card, FloatingInput, FloatingSelect, Label, SelectItem, Textarea } from './index.js';
import { MaterialIcon } from './MaterialIcon.js';
import {
  emptyTraveller,
  isChildRelationship,
  isTravellerEmpty,
  isTravellerValid,
  RELATIONSHIP_OPTIONS,
} from '@elara/client-core/lib/traveller-utils';

export interface OnboardingTravellerListControl {
  getTravellers(): AdditionalTraveller[];
  validate(): boolean;
}

interface OnboardingTravellerListProps {
  initial: AdditionalTraveller[];
  controlRef?: React.RefObject<OnboardingTravellerListControl | null>;
}

export function OnboardingTravellerList({ initial, controlRef }: OnboardingTravellerListProps) {
  const [travellers, setTravellers] = useState<AdditionalTraveller[]>(() =>
    initial.length > 0 ? initial : [emptyTraveller()],
  );
  const [showValidation, setShowValidation] = useState(false);
  // Track raw preference text per traveller to avoid round-tripping through
  // textToPreferences on every keystroke (which strips trailing newlines).
  const [prefTexts, setPrefTexts] = useState<Record<number, string>>(() => {
    const initial: Record<number, string> = {};
    for (let i = 0; i < travellers.length; i++) {
      initial[i] = preferencesToText(travellers[i]?.preferences);
    }
    return initial;
  });

  if (controlRef) {
    (controlRef as React.MutableRefObject<OnboardingTravellerListControl | null>).current = {
      getTravellers: () =>
        travellers
          .map((t, i) => ({ ...t, preferences: textToPreferences(prefTexts[i] ?? '') }))
          .filter((t) => !isTravellerEmpty(t)),
      validate() {
        const invalidIndex = travellers.findIndex((t) => !isTravellerEmpty(t) && !isTravellerValid(t));
        if (invalidIndex !== -1) {
          setShowValidation(true);
          return false;
        }
        return true;
      },
    };
  }

  const updateTraveller = (index: number, updates: Partial<AdditionalTraveller>) => {
    setTravellers((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates } as AdditionalTraveller;
      return next;
    });
  };

  const addTraveller = () => {
    setShowValidation(false);
    setTravellers((prev) => [...prev, emptyTraveller()]);
    setPrefTexts((prev) => ({ ...prev, [travellers.length]: '' }));
  };

  const removeTraveller = (index: number) => {
    setTravellers((prev) => prev.filter((_, i) => i !== index));
    setPrefTexts((prev) => {
      const next: Record<number, string> = {};
      for (const [k, v] of Object.entries(prev)) {
        const ki = Number(k);
        if (ki < index) next[ki] = v;
        else if (ki > index) next[ki - 1] = v;
      }
      return next;
    });
  };

  return (
    <div className="space-y-4">
      {travellers.map((traveller, index) => (
        <Card
          key={index}
          className={cn(
            'border p-5 space-y-4',
            showValidation && !isTravellerValid(traveller) ? 'border-destructive/50' : 'border-border',
          )}
        >
          {(() => {
            const invalid = showValidation && !isTravellerEmpty(traveller);
            const missingFirst = invalid && !traveller.firstName;
            const missingLast = invalid && !traveller.lastName;
            const missingRelationship = invalid && !traveller.relationship;
            return (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <FloatingInput
                    label="First name"
                    required
                    value={traveller.firstName}
                    onChange={(e) => updateTraveller(index, { firstName: e.target.value })}
                    error={missingFirst ? 'Required' : undefined}
                  />
                  <FloatingInput
                    label="Last name"
                    required
                    value={traveller.lastName}
                    onChange={(e) => updateTraveller(index, { lastName: e.target.value })}
                    error={missingLast ? 'Required' : undefined}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                  <FloatingSelect
                    label="Relationship"
                    required
                    value={traveller.relationship ?? ''}
                    onValueChange={(value) => updateTraveller(index, { relationship: value as Relationship })}
                    error={missingRelationship ? 'Required' : undefined}
                  >
                    {RELATIONSHIP_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </FloatingSelect>
                  {isChildRelationship(traveller.relationship) && (
                    <FloatingInput
                      label="Age (years)"
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={120}
                      value={traveller.age ?? ''}
                      onChange={(e) => {
                        const raw = e.target.value;
                        updateTraveller(index, { age: raw === '' ? undefined : Number(raw) });
                      }}
                    />
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Any preferences?</Label>
                  <Textarea
                    value={prefTexts[index] ?? ''}
                    onChange={(e) => setPrefTexts((prev) => ({ ...prev, [index]: e.target.value }))}
                    placeholder="e.g. Vegetarian, window seat, needs wheelchair access"
                    rows={2}
                    autoResize
                  />
                </div>

                <Button variant="link" onClick={() => removeTraveller(index)} className="text-destructive mb-0.5">
                  Remove
                </Button>
              </>
            );
          })()}
        </Card>
      ))}

      <Button variant="outline" className="w-full" onClick={addTraveller}>
        <MaterialIcon icon="add" />
        Add person
      </Button>
    </div>
  );
}
